import { Alert, AlertTitle, Button, Divider, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { DistrictAndAssociationPicker } from 'src/shared/components/form/districtAndAssociatonPicker';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useEffect, useState } from 'react';
import { Height } from '@mui/icons-material';
import { changeMemberAssociation } from 'src/shared/services/memberService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { Member } from '../models/member';
import { ChangeAssociationRequest } from '../models/changeAssocationRequest';
import { MemberChangeAssociationConfirm } from './memberChangeAssociationConfirm';

type MemberChangeAssociationModalProps = {
  member: Member;
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export const MemberChangeAssociationModal = (props: MemberChangeAssociationModalProps) => {
  const { member, modalOpen, onClose, onSave } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [associationId, setAssociationId] = useState<number>();
  const [hasUnpaidInvoices, setHasUnpaidInvoices] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [isFamilyMember, setIsFamilyMember] = useState<boolean>(false);
  const [disableEdit, setDisableEdit] = useState<boolean>(false);

  const { associationName } = useDistrictPickerHeader();

  useEffect(() => {
    setHasUnpaidInvoices(!!(member.currentInvoices && member.currentInvoices?.length));
    setIsFamilyMember(member.isFamilyMember && !member.isMainMember);
  }, [member]);

  useEffect(() => {
    setDisableEdit(isFamilyMember || hasUnpaidInvoices);
  }, [hasUnpaidInvoices, isFamilyMember]);

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = async () => {
    if (associationId && member.member_id) {
      setShowConfirmationDialog(false);
      setLoading(true);
      try {
        const data = { associationId } as ChangeAssociationRequest;
        await changeMemberAssociation(member.member_id, data);
        if (isMounted()) {
          toast.success(<>{t('member:changeAssociation.successMessage', { name: associationName })}</>);
          onSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    }
  };

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={onHandleClose}
        onSave={onHandleSave}
        maxWidth='sm'
        dialogTitle={t('member:changeAssociation.modalHeader', { name: associationName })}
        dialogSubTitle={`${member.firstName} ${member.lastName}`}
        padding={0}
        dialogContent={
          <>
            {disableEdit && (
              <Alert severity='error'>
                <AlertTitle> {t('member:changeAssociation.noChange')}</AlertTitle>
                {member.isFamilyMember && t('member:changeAssociation.isFamilyMember')}
                {hasUnpaidInvoices && !isFamilyMember && t('member:changeAssociation.hasUnpaidInvoices')}
              </Alert>
            )}
            {member.isMainMember && !disableEdit && (
              <>
                <Alert severity='info'>
                  <AlertTitle> {t('member:changeAssociation.isMainMemberTitle')}</AlertTitle>
                  {t('member:changeAssociation.isMainMember')}
                </Alert>
                <Divider />
              </>
            )}
            {!disableEdit && (
              <Box sx={{ pr: 3, py: 4, display: 'flex', flexDirection: 'row', justifyContent: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Height sx={{ color: (thm) => thm.palette.grey[300], fontSize: '7rem' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1, mb: 1.5 }} component='div'>
                    {t('member:changeAssociation.changeFrom')}
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 100, lineHeight: 1, mb: 3, ml: 1.5 }}>
                    {member.association?.localAss}
                  </Typography>
                  <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1, mb: 2 }} component='div'>
                    {t('member:changeAssociation.changeTo')}
                  </Typography>
                  <Box sx={{ ml: 1.5 }}>
                    <DistrictAndAssociationPicker callback={(x) => setAssociationId(x)} />
                  </Box>
                </Box>
              </Box>
            )}
            {associationId && member.association?.localAss_id === associationId && (
              <>
                <Divider />
                <Alert severity='warning'>{t('member:changeAssociation.changeError', { name: associationName })}</Alert>
              </>
            )}
          </>
        }
        dialogActions={
          <Stack spacing={1} direction='row'>
            <Button size='large' variant='outlined' color='primary' onClick={onHandleClose}>
              {t('common:button.cancel')}
            </Button>

            <LoadingButton
              size='large'
              disableElevation
              variant='contained'
              color='primary'
              onClick={() => setShowConfirmationDialog(true)}
              loading={loading}
              disabled={disableEdit || !associationId || member.association?.localAss_id === associationId}
            >
              {t('common:button.save')}
            </LoadingButton>
          </Stack>
        }
      />
      <MemberChangeAssociationConfirm
        modalOpen={showConfirmationDialog}
        onClose={() => onClose()}
        associationLabel={associationName}
        onConfirm={() => onHandleSave()}
      />
    </>
  );
};
